<template>
  <section
    v-if="!$apollo.queries.configuration.loading"
    class="flex flex-wrap flex-col lg:flex-row lg:align-center mt-80p text-white lg:py-24 bg-cover"
    :style="`background-image: url('${require('~/assets/img/bg-2.jpg')}');`"
  >
    <div class="container lg:grid lg:grid-cols-10 gap-x-4 px-4 py-8 ">
      <div class="flex flex-col col-span-4">
        <h1 class="leading-none lg:leading-normal lg:whitespace-no-wrap">
          Stel uw ideale poort samen
        </h1>

        <div v-if="!openForm">
          <h6 class="text-highlight pt-3 sm:pt-0 pb-3">
            {{ step }} / {{ totalSteps }}
          </h6>

          <gate
            v-if="activeGate"
            :gate="activeGate"
            :config="form"
            :show-pole="!form.installation || activeInstallation.title === 'Staanders'"
            :show-extra-left="form.elementLeft && activeElementLeft.title !== 'Geen'"
            :show-extra-right="form.elementRight && activeElementRight.title !== 'Geen'"
            class="mb-6 lg:hidden"
          />

          <!-- Gate -->
          <div v-if="step >= 1">
            <div class="flex items-baseline">
              <div class="flex w-32">
                <h3 :class="{'font-bold': step === 1}" class="text-sm font-body font-normal pt-4 pr-1">
                  Soort poort
                </h3>
                <button
                  v-if="step === 1"
                  class="flex justify-center items-center w-4 h-4 rounded-full text-secondary mt-3"
                  aria-label="Open info"
                  @click="tooltips.gate = true"
                >
                  <font-awesome-icon :icon="['far', 'info-circle']" aria-hidden="true"/>
                </button>
              </div>
              <span v-if="step >= 2" class="text-secondary text-subtitle pl-3 capitalize">
                {{ activeGate.title }}
                <button v-if="!success" class="text-xxs text-secondary lowercase underline pl-3" @click="toStep(1)">
                  Wijzigen
                </button>
              </span>
            </div>

            <div v-if="step === 1" class="py-4">
              <button
                v-for="gate in configuration.gates"
                :key="gate.id"
                :class="activeGate.id === gate.id ? ['bg-white text-primary'] : []"
                class="btn btn-white mr-4 mb-4"
                @click="form.gate = gate.id"
                v-text="gate.title"
              />

              <hr class="mb-4">

              <button
                class="btn btn-secondary mr-4 mb-4"
                @click="nextStep('gate', activeGate.id)"
              >
                Volgende
              </button>
            </div>

            <tooltip :step="1" :show="tooltips.gate" @close="tooltips.gate = false"/>
          </div>

          <!-- Wood type -->
          <div v-if="step >= 2">
            <div class="flex items-baseline">
              <div class="flex w-32">
                <h3 :class="{'font-bold': step === 2}" class="text-sm font-body font-normal pt-4 pr-1">
                  Houtsoort
                </h3>
                <button
                  v-if="step === 2"
                  class="flex justify-center items-center w-4 h-4 rounded-full text-secondary mt-3"
                  aria-label="Open info"
                  @click="tooltips.wood = true"
                >
                  <font-awesome-icon :icon="['far', 'info-circle']" aria-hidden="true"/>
                </button>
              </div>
              <span v-if="step > 2" class="text-secondary text-subtitle pl-3">
                {{ activeWood.title }}
                <button v-if="!success" class="text-xxs text-secondary lowercase underline pl-3" @click="toStep(2)">
                  Wijzigen
                </button>
              </span>
            </div>

            <div v-if="step === 2" class="py-4">
              <div class="options flex flex-wrap">
                <div
                  v-for="option in activeGate.wood_types"
                  :key="option.id"
                  class="flex items-center pr-4 mb-4"
                >
                  <div class="pr-4 flex items-center gap-1">
                    <input
                      :id="`wood_${option.id}`"
                      v-model="form.woodType"
                      :value="option.id"
                      type="radio"
                      class="option transition duration-200"
                      @change="form.woodType === option.id"
                    >
                    <label :for="`wood_${option.id}`" class="flex items-center gap-3">
                      <span>{{ option.title }}</span>
                      <img
                        v-if="option.image"
                        :src="option.image.url"
                        :alt="option.title"
                        class="h-16 w-16 rounded-full object-cover"
                      >
                    </label>
                  </div>
                </div>
              </div>

              <hr class="my-4">

              <button
                class="btn btn-secondary mr-4 mb-4"
                :disabled="!form.woodType"
                @click="nextStep"
              >
                Volgende
              </button>
            </div>

            <tooltip :step="2" :show="tooltips.wood" @close="tooltips.wood = false">
              <div
                v-for="option in configuration.wood"
                :key="option.id"
                class="py-3"
              >
                <h4 class="pb-0" v-text="option.title"/>
                <div v-html="option.text"/>
              </div>
            </tooltip>
          </div>

          <!-- Thickness -->
          <div v-if="step >= 3">
            <div class="flex items-baseline">
              <div class="flex w-32">
                <h3 :class="{'font-bold': step === 3}" class="text-sm font-body font-normal pt-4 pr-1">
                  Dikte
                </h3>
                <button
                  v-if="step === 3"
                  class="flex justify-center items-center w-4 h-4 rounded-full text-secondary mt-3"
                  aria-label="Open info"
                  @click="tooltips.thickness = true"
                >
                  <font-awesome-icon :icon="['far', 'info-circle']" aria-hidden="true"/>
                </button>
              </div>
              <span v-if="step > 3" class="text-secondary text-subtitle pl-3">
                {{ form.thickness }}mm
                <button
                  v-if="!success && hasMultipleThicknesses"
                  class="text-xxs text-secondary lowercase underline pl-3"
                  @click="toStep(3)"
                >
                  Wijzigen
                </button>
              </span>
            </div>

            <div v-if="step === 3" class="py-4">
              <button
                v-for="thickness in activeGate.thicknesses"
                :key="thickness"
                class="btn btn-secondary mr-4 mb-4"
                :class="form.thickness === thickness ? ['bg-secondary text-white'] : []"
                @click="form.thickness = thickness"
                v-text="`${thickness}mm`"
              />

              <hr class="my-4">

              <button
                class="btn btn-secondary mr-4 mb-4"
                :disabled="!form.thickness"
                @click="nextStep"
              >
                Volgende
              </button>
            </div>

            <tooltip :step="3" :show="tooltips.thickness" @close="tooltips.thickness = false"/>
          </div>

          <!-- Width -->
          <div v-if="step >= 4">
            <div class="flex items-baseline">
              <div class="flex w-32">
                <h3 :class="{'font-bold': step === 4}" class="text-sm font-body font-normal pt-4 pr-1">
                  Breedte
                </h3>
                <button
                  v-if="step === 4"
                  class="flex justify-center items-center w-4 h-4 rounded-full text-secondary mt-3"
                  aria-label="Open info"
                  @click="tooltips.width = true"
                >
                  <font-awesome-icon :icon="['far', 'info-circle']" aria-hidden="true"/>
                </button>
              </div>
              <span v-if="step > 4" class="text-secondary text-subtitle pl-3">
                {{ form.width }}mm
                <button v-if="!success" class="text-xxs text-secondary lowercase underline pl-3" @click="toStep(4)">
                  Wijzigen
                </button>
              </span>
            </div>

            <div v-if="step === 4 && !form.customWidth" class="py-4">
              <button
                v-for="width in filteredWidths"
                :key="width"
                class="btn btn-secondary mr-4 mb-4"
                :class="form.width === width ? ['bg-secondary text-white'] : []"
                @click="form.customWidth = false; form.width = width"
                v-text="`${width}mm`"
              />
              <div v-if="activeGate.widths.includes('custom')" class="flex items-start mr-4 mb-4">
                <button
                  class="btn btn-secondary"
                  @click="form.customWidth = true"
                  v-text="'Geef uw eigen breedte op'"
                />
              </div>

              <hr class="my-4">

              <button
                class="btn btn-secondary mr-4 mb-4"
                :disabled="!form.width"
                @click="nextStep"
              >
                Volgende
              </button>
            </div>

            <div v-if="step === 4 && form.customWidth" class="py-4">
              <label class="w-full flex text-base font-bold">
                Breedte in mm (tussen 3000 mm en 4500 mm)
                <button
                  class="relative flex justify-center items-center w-4 h-4 rounded-full text-secondary ml-1 group"
                  aria-label="Open info"
                >
                  <font-awesome-icon :icon="['far', 'info-circle']" aria-hidden="true"/>
                  <span
                    class="absolute bg-primary opacity-0 pointer-events-none group-hover:opacity-100 shadow-lg bottom-full right-0 p-4 w-64"
                  >
                    Voor niet-standaard maten berekenen wij een toeslag van 10%
                  </span>
                </button>
              </label>

              <input
                v-model.number="form.width"
                type="number"
                min="3000"
                max="4500"
                placeholder="Vul uw eigen breedte in..."
                aria-label="Gewenste breedte"
                class="w-full text-base font-bold bg-transparent shadow-b-s-2 hover:shadow-b-s-3 focus:shadow-b-s-3 transition-all duration-300 pt-4 pl-3 mb-5"
              >
              <p v-if="form.width && !isCorrectCustomWidth" class="text-sm mb-3">
                De gekozen breedte ligt niet tussen de 3000 en 4500 mm. In overleg is tot 5000 mm breedte op verzoek
                mogelijk, neem hiervoor
                <nuxt-link to="/contact">contact</nuxt-link>
                met ons op.
              </p>
              <button
                class="btn btn-secondary mr-4 mb-4"
                :disabled="!isCorrectCustomWidth"
                @click="nextStep"
              >
                Volgende
              </button>
              <button
                class="btn btn-secondary mr-4 mb-4"
                @click="form.customWidth = false"
              >
                Kies standaard maat
              </button>
            </div>

            <tooltip :step="4" :show="tooltips.width" @close="tooltips.width = false"/>
          </div>

          <!-- Installation -->
          <div v-if="step >= 5">
            <div class="flex items-baseline">
              <div class="flex w-32">
                <h3 :class="{'font-bold': step === 5}" class="text-sm font-body font-normal pt-4 pr-1">
                  Montage
                </h3>
                <button
                  v-if="step === 5"
                  class="flex justify-center items-center w-4 h-4 rounded-full text-secondary mt-3"
                  aria-label="Open info"
                  @click="tooltips.mounting = true"
                >
                  <font-awesome-icon :icon="['far', 'info-circle']" aria-hidden="true"/>
                </button>
              </div>
              <span v-if="step > 5" class="text-secondary text-subtitle pl-3">
                {{ activeInstallation.title }}
                <button v-if="!success" class="text-xxs text-secondary lowercase underline pl-3" @click="toStep(5)">
                  Wijzigen
                </button>
              </span>
            </div>

            <div v-if="step === 5" class="py-4">
              <div class="options flex flex-col">
                <div
                  v-for="option in configuration.installation"
                  :key="option.id"
                  class="pb-4"
                >
                  <input
                    :id="`installation_${option.id}`"
                    v-model="form.installation"
                    :value="option.id"
                    type="radio"
                    class="option transition duration-200"
                    @click="form.installation === option.id"
                  >
                  <label :for="`installation_${option.id}`" v-text="option.title"/>
                </div>
              </div>

              <div v-if="form.installation && activeInstallation.title === 'Aan bestaand metselwerk'">
                <label class="w-full flex text-base font-bold">
                  Breedtemaat van de gemetselde staanders (in mm)
                </label>

                <input
                  v-model.number="form.intermediateSize"
                  type="number"
                  min="1"
                  placeholder="Vul de breedtemaat in…"
                  aria-label="Tussenmaat"
                  class="w-full text-base font-bold bg-transparent shadow-b-s-2 hover:shadow-b-s-3 focus:shadow-b-s-3 transition-all duration-300 pt-4 pl-3 mb-5"
                >
              </div>

              <hr v-else class="my-4">

              <button
                class="btn btn-secondary mr-4 mb-4"
                :disabled="!form.installation || (isMasonry && !form.intermediateSize)"
                @click="nextStep"
              >
                Volgende
              </button>
            </div>

            <tooltip :step="5" :show="tooltips.mounting" @close="tooltips.mounting = false"/>
          </div>

          <!-- Element left -->
          <div v-if="step >= 6">
            <div class="flex items-baseline">
              <div class="flex w-32">
                <h3 :class="{'font-bold': step === 6}" class="text-sm font-body font-normal pt-4 pr-1">
                  Extra element links
                </h3>
                <button
                  v-if="step === 6"
                  class="flex justify-center items-center w-4 h-4 rounded-full text-secondary mt-3"
                  aria-label="Open info"
                  @click="tooltips.extra_left = true"
                >
                  <font-awesome-icon :icon="['far', 'info-circle']" aria-hidden="true"/>
                </button>
              </div>
              <span v-if="step > 6" class="flex flex-col items-start text-secondary text-subtitle pl-3">
                {{ activeElementLeft.title }}
                <button v-if="!success" class="text-xxs text-secondary lowercase underline" @click="toStep(6)">
                  Wijzigen
                </button>
              </span>
            </div>

            <div v-if="step === 6" class="py-4">
              <div class="options flex flex-col">
                <div
                  v-for="option in activeGate.extra_elements"
                  :key="option.id"
                  class="pb-4"
                >
                  <input
                    :id="`element_left_${option.id}`"
                    v-model="form.elementLeft"
                    type="radio"
                    :value="option.id"
                    class="option transition duration-200"
                    @click="form.elementLeft === option.id"
                  >
                  <label :for="`element_left_${option.id}`" v-text="option.title"/>
                </div>
              </div>

              <hr class="my-4">

              <button
                class="btn btn-secondary mr-4 mb-4"
                :disabled="!form.elementLeft"
                @click="nextStep"
              >
                Volgende
              </button>
            </div>

            <tooltip :step="6" :show="tooltips.extra_left" @close="tooltips.extra_left = false"/>
          </div>

          <!-- Element right -->
          <div v-if="step >= 7">
            <div class="flex items-baseline">
              <div class="flex w-32">
                <h3 :class="{'font-bold': step === 7}" class="text-sm font-body font-normal pt-4 pr-1">
                  Extra element rechts
                </h3>
                <button
                  v-if="step === 7"
                  class="flex justify-center items-center w-4 h-4 rounded-full text-secondary mt-3"
                  aria-label="Open info"
                  @click="tooltips.extra_right = true"
                >
                  <font-awesome-icon :icon="['far', 'info-circle']" aria-hidden="true"/>
                </button>
              </div>
              <span v-if="step > 7" class="flex flex-col items-start text-secondary text-subtitle pl-3">
                {{ activeElementRight.title }}
                <button v-if="!success" class="text-xxs text-secondary lowercase underline" @click="toStep(7)">
                  Wijzigen
                </button>
              </span>
            </div>

            <div v-if="step === 7" class="py-4">
              <div class="options flex flex-col">
                <div
                  v-for="option in activeGate.extra_elements"
                  :key="option.id"
                  class="pb-4"
                >
                  <input
                    :id="`element_right_${option.id}`"
                    v-model="form.elementRight"
                    type="radio"
                    :value="option.id"
                    class="option transition duration-200"
                    @click="form.elementRight === option.id"
                  >
                  <label :for="`element_right_${option.id}`" v-text="option.title"/>
                </div>
              </div>

              <hr class="my-4">

              <button
                class="btn btn-secondary mr-4 mb-4"
                :disabled="!form.elementRight"
                @click="nextStep"
              >
                Volgende
              </button>
            </div>

            <tooltip :step="7" :show="tooltips.extra_right" @close="tooltips.extra_right = false"/>
          </div>

          <!-- Automation -->
          <div v-if="step >= 8">
            <div class="flex items-baseline">
              <div class="flex w-32">
                <h3 :class="{'font-bold': step === 8}" class="text-sm font-body font-normal pt-4 pr-1">
                  Automatisering
                </h3>
                <button
                  v-if="step === 8"
                  class="flex justify-center items-center w-4 h-4 rounded-full text-secondary mt-3"
                  aria-label="Open info"
                  @click="tooltips.automation = true"
                >
                  <font-awesome-icon :icon="['far', 'info-circle']" aria-hidden="true"/>
                </button>
              </div>
              <span v-if="step > 8" class="text-secondary text-subtitle pl-3">
                {{ activeAutomation.title }}
                <button v-if="!success" class="text-xxs text-secondary lowercase underline pl-3" @click="toStep(8)">
                  Wijzigen
                </button>
              </span>
            </div>

            <div v-if="step === 8" class="py-4">
              <div class="options flex flex-col">
                <div
                  v-for="option in activeGate.automations"
                  :key="option.id"
                  class="pb-4"
                >
                  <input
                    :id="`automation_${option.id}`"
                    v-model="form.automation"
                    :value="option.id"
                    type="radio"
                    class="option transition duration-200"
                    @click="form.automation === option.id"
                  >
                  <label :for="`automation_${option.id}`" v-text="option.title"/>
                </div>
              </div>

              <hr class="my-4">

              <button
                class="btn btn-secondary mr-4 mb-4"
                :disabled="!form.automation"
                @click="nextStep"
              >
                Volgende
              </button>
            </div>

            <tooltip :step="8" :show="tooltips.automation" @close="tooltips.automation = false">
              <div
                v-for="option in activeGate.automations"
                :key="option.id"
                class="py-3"
              >
                <h4 class="pb-0" v-text="option.title"/>
                <div v-html="option.text"/>
              </div>
            </tooltip>
          </div>

          <!-- Automation extras -->
          <div v-if="step >= 9 && parseInt(form.automation) !== 1">
            <div class="flex items-baseline">
              <div class="flex w-32">
                <h3 :class="{'font-bold': step === 9}" class="text-sm font-body font-normal pt-4 pr-1">
                  Extra opties automatisering
                </h3>
                <button
                  v-if="step === 9"
                  class="flex justify-center items-center w-4 h-4 rounded-full text-secondary mt-3"
                  aria-label="Open info"
                  @click="tooltips.automation_options = true"
                >
                  <font-awesome-icon :icon="['far', 'info-circle']" aria-hidden="true"/>
                </button>
              </div>
              <div v-if="step > 9" class="w-80 text-secondary text-subtitle pl-3">
                {{ form.automationOptions.length === 0 ? 'Niets geselecteerd' : '' }}
                <p v-for="(option, i) in activeAutomationOptions" :key="i" class="mr-2">
                  - {{ selectedOptionObject(option).amount }}x {{ option.title }}
                </p>
                <button v-if="!success" class="text-xxs text-secondary lowercase underline" @click="toStep(9)">
                  Wijzigen
                </button>
              </div>
            </div>

            <div v-if="step === 9" class="py-4">
              <div class="options flex flex-col">
                <div
                  v-for="option in configuration.automation_options"
                  :key="option.id"
                  class="pb-4"
                >
                  <div class="flex space-x-3">
                    <div>
                      <input
                        :id="`option_${option.id}`"
                        type="checkbox"
                        :checked="selectedOptionObject(option)"
                        class="checkbox transition duration-200"
                        :disabled="optionDisabled(option)"
                        @change="selectOption(option)"
                      >
                      <label :for="`option_${option.id}`" v-text="option.title"/>
                    </div>
                    <input
                      v-if="option.multiple && selectedOptionObject(option)"
                      v-model="selectedOptionObject(option).amount"
                      type="number"
                      min="301"
                      max="449"
                      placeholder="Aantal"
                      aria-label="Aantal"
                      class="w-full text-base font-bold bg-transparent shadow-b-s-2 hover:shadow-b-s-3 focus:shadow-b-s-3 transition-all duration-300 pt-4 pl-3 mb-5"
                    >
                  </div>
                </div>
              </div>

              <hr class="mb-4">

              <button
                class="btn btn-secondary mr-4 mb-4"
                @click="nextStep"
                v-text="'Volgende'"
              />
            </div>

            <tooltip :step="9" :show="tooltips.automation_options" @close="tooltips.automation_options = false">
              <div
                v-for="option in configuration.automation_options"
                :key="option.id"
                class="py-3"
              >
                <h4 class="pb-0" v-text="option.title"/>
                <div v-html="option.text"/>
              </div>
            </tooltip>
          </div>

          <!-- Options -->
          <div v-if="step >= 10">
            <div class="flex items-baseline">
              <div class="flex w-32">
                <h3 :class="{'font-bold': step === 8}" class="text-sm font-body font-normal pt-4 pr-1">
                  Opties
                </h3>
                <button
                  v-if="step === 10"
                  class="flex justify-center items-center w-4 h-4 rounded-full text-secondary mt-3"
                  aria-label="Open info"
                  @click="tooltips.options = true"
                >
                  <font-awesome-icon :icon="['far', 'info-circle']" aria-hidden="true"/>
                </button>
              </div>
              <span v-if="step > 10" class="flex flex-col items-start text-secondary text-subtitle pl-3">
                {{ form.options.length === 0 ? 'Niets geselecteerd' : '' }}
                <span v-for="(option, i) in activeOptions" :key="i" class="mr-2">
                  - {{ option.title }}
                </span>
                <button v-if="!success" class="text-xxs text-secondary lowercase underline" @click="toStep(10)">
                  Wijzigen
                </button>
              </span>
            </div>

            <div v-if="step === 10" class="py-4">
              <div class="options flex flex-col">
                <div
                  v-for="option in activeGate.options"
                  :key="option.id"
                  class="pb-4"
                >
                  <input
                    :id="`option_${option.id}`"
                    v-model="form.options"
                    :value="option.id"
                    type="checkbox"
                    class="checkbox transition duration-200"
                    :disabled="optionDisabled(option)"
                  >
                  <label :for="`option_${option.id}`" v-text="option.title"/>
                </div>
              </div>

              <hr class="mb-4">

              <button
                class="btn btn-secondary mr-4 mb-4"
                @click="nextStep"
                v-text="'Volgende'"
              />
            </div>

            <tooltip :step="10" :show="tooltips.options" @close="tooltips.options = false">
              <div
                v-for="option in activeGate.options"
                :key="option.title"
                class="py-3"
              >
                <h4 class="pb-0" v-text="option.title"/>
                <div v-html="option.text"/>
              </div>
            </tooltip>
          </div>

          <div>
            <h2 v-if="step === 1" class="text-white text-lg pt-5">
              Vanafprijs van uw poort op maat:<br>
              <span class="block sm:inline text-secondary text-title">
                &euro; {{ money(activeGate?.price_3_00m - priceIndicationLower) }}
              </span>
            </h2>
            <small class="block text-highlight mt-1">Buitenmaat {{ outsideWidth }} mm</small>
            <div class="flex">
              <small v-if="activeGate.lowest" class="block text-highlight">Hoogte: {{ activeGate.lowest }}
                mm&nbsp;</small>
              <small v-if="activeGate.highest" class="block text-highlight">- {{ activeGate.highest }} mm</small>
            </div>
            <small class="block text-secondary underline mt-4" role="button" @click="reset">
              Herstel gekozen configuratie
            </small>
          </div>
        </div>

        <div v-if="step >= totalSteps" class="pt-12 lg:pt-6">
          <button
            v-if="!success"
            :disabled="openForm"
            :class="{'bg-secondary': openForm, 'cursor-default': openForm}"
            class="self-end border border-secondary text-white font-bold hover:bg-secondary rounded-none w-full text-lg p-6 transition duration-200 form-btn"
            @click="openForm = true"
          >
            Prijsopgave per mail ontvangen
          </button>

          <p v-if="!openForm" class="mt-3">
            <em>
              De opgevraagde prijsopgave is geheel vrijblijvend en verplicht mij tot niets.
            </em>
          </p>

          <form v-if="openForm && !success" class="bg-white text-black px-10 py-8" @submit.prevent="submit">
            <button type="button" class="btn btn-secondary mb-8" @click="toStep(totalSteps)">Terug</button>

            <div class="mb-12 shadow-b-2 hover:shadow-b-3 focus-within:shadow-b-3 transition-all duration-300">
              <label for="name" class="font-bold text-lg">Naam</label>
              <input
                id="name"
                v-model="form.name"
                type="text"
                required
                placeholder="Vul uw naam in"
                class="w-full placeholder-primary-lighten1 transition-all duration-300 pb-3 mt-4"
              >
            </div>

            <div class="mb-12 shadow-b-2 hover:shadow-b-3 focus-within:shadow-b-3 transition-all duration-300">
              <label for="email" class="font-bold text-lg">E-mailadres</label>
              <input
                id="email"
                v-model="form.email"
                type="email"
                required
                placeholder="Vul uw e-mailadres in"
                class="w-full placeholder-primary-lighten1 transition-all duration-300 pb-3 mt-4"
              >
            </div>

            <div class="mb-12 shadow-b-2 hover:shadow-b-3 focus-within:shadow-b-3 transition-all duration-300">
              <label for="phone" class="font-bold text-lg">Telefoonnummer</label>
              <input
                id="phone"
                v-model="form.phone"
                type="tel"
                placeholder="Vul uw e-telefoonnummer in"
                class="w-full placeholder-primary-lighten1 transition-all duration-300 pb-3 mt-4"
              >
            </div>

            <div class="mb-12 shadow-b-2 hover:shadow-b-3 focus-within:shadow-b-3 transition-all duration-300">
              <label for="city" class="font-bold text-lg">Woonplaats</label>
              <input
                id="city"
                v-model="form.city"
                type="text"
                placeholder="Vul uw woonplaats in"
                class="w-full placeholder-primary-lighten1 transition-all duration-300 pb-3 mt-4"
                required
              >
            </div>

            <div class="mb-12 shadow-b-2 hover:shadow-b-3 focus-within:shadow-b-3 transition-all duration-300">
              <label for="message" class="font-bold text-lg">Aanvullende wensen/opmerkingen</label>
              <textarea
                id="message"
                v-model="form.message"
                placeholder="Heeft u nog opmerkingen?"
                class="w-full placeholder-primary-lighten1 transition-all duration-300 pb-3 mt-4"
              />
            </div>

            <div class="mb-3">
              <input
                id="deliveryIndication"
                v-model="form.sendDeliveryIndication"
                type="checkbox"
                class="checkbox transition duration-200 dark"
              >
              <label for="deliveryIndication">Ik ontvang ook graag een vrijblijvende indicatie van levering en montage
                op locatie op basis van mijn ingevulde woonplaats</label>
            </div>

            <button :disabled="submitting" type="submit" class="btn btn-primary mb-40">
              Verzenden
            </button>
          </form>

          <div v-if="success">
            <hr class="mb-6">
            <p class="text-lg">Bedankt! Uw aanvraag is verzonden.</p>
            <button
              class="btn btn-secondary mt-4"
              @click="reset"
            >
              Start nieuwe configuratie
            </button>
          </div>
        </div>
      </div>

      <div class="hidden lg:flex col-span-5 col-start-6">
        <gate
          v-if="activeGate"
          :gate="activeGate"
          :config="form"
          :show-pole="!form.installation || activeInstallation.title === 'Staanders'"
          :show-extra-left="form.elementLeft && activeElementLeft.title !== 'Geen'"
          :show-extra-right="form.elementRight && activeElementRight.title !== 'Geen'"
          class="sticky top-80"
        />
      </div>
    </div>
  </section>
</template>

<script>
import Gate from '~/components/configurator/Gate';
import Tooltip from '~/components/configurator/Tooltip';
import configurationQuery from '~/graphql/queries/configuration.gql';
import requestQuotationMutation from '~/graphql/mutations/requestQuotation.gql';

export default {
  components: {
    Tooltip,
    Gate,
  },

  apollo: {
    configuration: {
      query: configurationQuery,
      update: data => data, // needed because of the multi-query
      result() {
        // set the gate based on the slug
        if (this.$route.params.slug) {
          const gate = this.configuration.gates.find(gate => gate.slug === this.$route.params.slug);

          if (gate) {
            this.form.gate = gate.id;
          }
        }
      },
    },
  },

  data: () => ({
    form: {
      gate: null,
      thickness: null,
      width: null,
      customWidth: false,
      installation: null,
      intermediateSize: null,
      elementLeft: null,
      elementRight: null,
      options: [],
      automation: null,
      woodType: null,
      automationOptions: [],
      name: null,
      email: null,
      message: null,
      phone: null,
      city: null,
      sendCopy: true,
      sendDeliveryIndication: false,
    },
    tooltips: {
      gate: false,
      wood: false,
      thickness: false,
      width: false,
      mounting: false,
      extra_left: false,
      extra_right: false,
      options: false,
      automation: false,
      automation_options: false,
    },
    submitting: false,
    success: false,
    step: 1,
    totalSteps: 11,
    openForm: false,
    priceIndicationLower: 500,
  }),

  computed: {
    activeGate() {
      if (this.form.gate) {
        return this.configuration.gates.find(gate => gate.id === this.form.gate);
      }

      return this.configuration.gates[0];
    },

    activeInstallation() {
      if (this.form.installation) {
        return this.configuration.installation.find(install => install.id === this.form.installation);
      }

      return this.configuration.installation[1];
    },

    activeWood() {
      if (this.form.woodType) {
        return this.activeGate.wood_types.find(type => type.id === this.form.woodType);
      }

      return this.activeGate.wood_types[0];
    },

    filteredWidths() {
      return this.activeGate.widths.filter(width => !Number.isNaN(Number(width)));
    },

    activeOptions() {
      return this.activeGate.options.filter(option => this.form.options.includes(option.id));
    },

    activeElementLeft() {
      if (this.form.elementLeft) {
        return this.activeGate.extra_elements.find(elem => elem.id === this.form.elementLeft);
      }

      return this.activeGate.extra_elements.find(elem => elem.title === 'Geen');
    },

    activeElementRight() {
      if (this.form.elementRight) {
        return this.activeGate.extra_elements.find(elem => elem.id === this.form.elementRight);
      }

      return this.activeGate.extra_elements.find(elem => elem.title === 'Geen');
    },

    activeAutomation() {
      if (this.form.automation) {
        return this.activeGate.automations.find(a => a.id === this.form.automation);
      }

      return this.activeGate.automations[0];
    },

    activeAutomationOptions() {
      return this.configuration.automation_options.filter(option => this.form.automationOptions.find(o => parseInt(o.id) === parseInt(option.id)));
    },

    hasMultipleThicknesses() {
      return this.activeGate.thicknesses.length > 1;
    },

    isCorrectCustomWidth() {
      const width = parseInt(this.form.width);

      return width >= 3000 && width <= 4500;
    },

    isMasonry() {
      return !this.$apollo.queries.configuration.loading && this.activeInstallation.title === 'Aan bestaand metselwerk';
    },

    // returns the total width of the configuration in centimeters
    totalWidth() {
      // default pole width
      let poleWidth = 190; // mm

      if (this.activeInstallation.title !== 'Staanders') {
        // use custom mounting type
        poleWidth = this.form.intermediateSize; // mm
      }

      // start with selected gate width
      let width = Number(this.form.width); // mm

      // base poles
      if (this.form.installation) {
        width += 2 * poleWidth;
      }

      if (this.activeElementLeft.title !== 'Geen') {
        // extra pole on the left
        width += poleWidth;

        // width of element
        width += this.getExtraElementWidth(this.activeElementLeft);
      }

      if (this.activeElementRight.title !== 'Geen') {
        // extra pole on the right
        width += poleWidth;

        // width of element
        width += this.getExtraElementWidth(this.activeElementRight);
      }

      return width;
    },

    outsideWidth() {
      return this.step <= 4 && !this.form.width ? '--' : this.totalWidth;
    },
  },

  watch: {
    step() {
      // Skip step 3 (thickness) when just a single thickness is available
      if (this.step === 3 && !this.hasMultipleThicknesses) {
        this.form.thickness = this.activeGate.thicknesses[0];
        this.nextStep();
      }

      // skip step 9 (automation options) if no automation is selected
      if (this.step === 9 && this.activeAutomation.title === 'Geen') {
        this.nextStep();
      }
    },

    form: {
      deep: true,
      handler() {
        // reset intermediate size if installation is not suitable anymore
        if (!this.isMasonry && this.form.intermediateSize) {
          this.form.intermediateSize = null;
        }

        // automation options when there is no automation (1 = Geen)
        if (this.form.automationOptions.length && parseInt(this.form.automation) === 1) {
          this.form.automationOptions = [];
        }

        // reset extra element choices if installation is not suitable anymore
        if (this.isMasonry && this.step === 5) {
          if (this.form.elementLeft) {
            this.form.elementLeft = null;
          }

          if (this.form.elementRight) {
            this.form.elementRight = null;
          }
        }

        this.saveProgress();
      },
    },
  },

  mounted() {
    if (localStorage.progress) {
      const step = parseInt(localStorage.step);

      if (!isNaN(step)) {
        this.step = step;
      } else {
        this.resetProgress();
      }

      try {
        this.form = JSON.parse(localStorage.progress);
      } catch (e) {
        this.resetProgress();
      }
    }
  },

  methods: {
    toStep(index) {
      this.step = index;
      this.openForm = false;
    },

    nextStep(key, value) {
      if (value) {
        this.form[key] = value;
      }

      this.step++;

      this.saveProgress();
    },

    saveProgress() {
      localStorage.setItem('step', this.step);
      localStorage.setItem('progress', JSON.stringify(this.form));
    },

    resetProgress() {
      localStorage.removeItem('step');
      localStorage.removeItem('progress');
    },

    reset() {
      // reset local storage
      this.resetProgress();

      // reload the page
      window.location.reload(true);
    },

    submit() {
      try {
        this.submitting = true;

        this.$apollo.mutate({
          mutation: requestQuotationMutation,
          variables: {
            ...this.form,
            totalWidth: this.totalWidth,
          },
        });

        this.success = true;
        this.scrollToTop();
      } catch (e) {
        console.error(e);
      } finally {
        this.submitting = false;
      }

      if (this.success) {
        this.resetProgress();
      }
    },

    money(float) {
      return (float || 0)
        .toFixed(2)
        .replace('.', ',')
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
        .replace(',00', '');
    },

    selectedOptionObject(option) {
      return this.form.automationOptions.find(o => o.id === option.id);
    },

    selectOption(option) {
      const tempAutomationOptions = this.form.automationOptions || [];

      const existingIndex = tempAutomationOptions.findIndex(o => o.id === option.id);
      if (existingIndex !== -1) {
        tempAutomationOptions.splice(existingIndex, 1);
      }

      tempAutomationOptions.push({
        id: option.id,
        amount: 1,
      });

      this.form.automationOptions = tempAutomationOptions;
    },

    optionDisabled(option) {
      const key = this.form.options.indexOf(option.id.toString());

      // only allow 'beton midden aanslag' if no automation is selected
      if (option.title.toLowerCase().includes('aanslag') && this.activeAutomation.title !== 'Geen') {
        if (key >= 0) {
          this.form.options.splice(key, 1);
        }

        return true;
      }

      // only allow 'sponningen in staanders' if 'staanders' mounting is selected
      if (option.title.toLowerCase().includes('sponningen') && this.activeInstallation.title !== 'Staanders') {
        if (key >= 0) {
          this.form.options.splice(key, 1);
        }

        return true;
      }

      return false;
    },

    getExtraElementWidth(element) {
      switch (element.title) {
        case 'Vast element - 1000mm':
        case 'Looppoort - 1000mm':
          return 1000; // mm

        case 'Looppoort - 1200mm':
          return 1200; // mm

        default:
          return 0; // mm
      }
    },

    scrollToTop() {
      window.scrollTo(0, 0);
    },
  },
};
</script>

<style scoped>
input[type=checkbox]:checked.dark {
  @apply bg-primary;
}

input[disabled],
input[disabled] + label {
  cursor: not-allowed;
  color: lightgray;
}

button[disabled]:not(.form-btn) {
  cursor: not-allowed;
  background: none;
  border-color: lightgray;
  color: lightgray;
}
</style>
